import React, { useState, useEffect, useCallback } from "react";
import {
  TableColumn,
  StandardizedTable,
  formatCurrency as stdFormatCurrency,
  TrendIndicator,
  TableRow,
} from "@/components/ui/standardized-table";
import { useGlobalFilter } from "@/features/filters/filter.context";
import { formatDateToISO } from "@/features/filters/filter.types";
import { useAccount } from "@/contexts/account.context";
import type { TableQueryDTO } from "@/types/table-query";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { useDrilldownOptional } from "@/features/drilldown";
import { getCategoryByStateApiV1CategoryByStateGet } from "@/backend-sdk/client/sdk.gen";
import type {
  CategoryByRegionResponse,
  GetCategoryByStateApiV1CategoryByStateGetData,
} from "@/backend-sdk/client/types.gen";

type CategoryByStateQuery =
  GetCategoryByStateApiV1CategoryByStateGetData["query"];
type CategoryByStateCrossFilterKey = Extract<
  keyof CategoryByStateQuery,
  `filter_${string}`
>;
type CategoryByStateCrossFilters = Pick<
  CategoryByStateQuery,
  CategoryByStateCrossFilterKey
>;

const getCategoryByStateCrossFilters = (
  filters: Record<string, string> | undefined,
): Partial<CategoryByStateCrossFilters> => ({
  filter_category: filters?.filter_category,
  filter_city: filters?.filter_city,
  filter_product: filters?.filter_product,
  filter_search_term: filters?.filter_search_term,
  filter_device: filters?.filter_device,
  filter_date: filters?.filter_date,
});

const CategoriasPorEstadoTable: React.FC = () => {
  const { filter: filterState } = useGlobalFilter();
  const { selectedAccount } = useAccount();
  const drilldown = useDrilldownOptional();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<CategoryByRegionResponse | null>(null);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [tableQuery, setTableQuery] = useState<TableQueryDTO>({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const startDate = formatDateToISO(filterState.dateRange.startDate);
        const endDate = formatDateToISO(filterState.dateRange.endDate);
        const comparison =
          filterState.comparison?.type === "previousYear"
            ? "previous_year"
            : "previous_period";
        const customerUuid =
          selectedAccount?.customer_uuid ||
          localStorage.getItem("active_customer_uuid") ||
          undefined;

        if (!customerUuid) {
          setData(null);
          return;
        }

        const { data: result } =
          await getCategoryByStateApiV1CategoryByStateGet({
            query: {
              customer_uuid: customerUuid,
              start_date: startDate,
              end_date: endDate,
              comparison,
              page,
              page_size: pageSize,
              q: tableQuery.q,
              sort_by: tableQuery.sortBy,
              sort_dir: tableQuery.sortDir,
              filters: tableQuery.filters?.length
                ? JSON.stringify(tableQuery.filters)
                : undefined,
              ...getCategoryByStateCrossFilters(drilldown?.apiFilters),
            },
            throwOnError: true,
          });
        setData(result);
      } catch (error) {
        console.error("Erro ao buscar categoria por estado:", error);
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [filterState, selectedAccount, page, tableQuery, drilldown?.apiFilters]);

  // Resetar paginação ao mudar filtro/conta ou cross-filters
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPage(1);
  }, [filterState, selectedAccount, drilldown?.apiFiltersKey]);

  // Handler para clique na linha (cross-filtering com Ctrl+click)
  const handleRowClick = useCallback(
    (row: TableRow, index: number, event?: React.MouseEvent) => {
      if (!drilldown) return;
      const regionName = row.region as string;
      if (!regionName || regionName === "N/D") return;

      const isMultiSelect = event?.ctrlKey || event?.metaKey;
      drilldown.selectRow("region", regionName, regionName, isMultiSelect);
    },
    [drilldown],
  );

  // Verifica se uma linha está selecionada
  const isRowSelected = useCallback(
    (row: TableRow, index: number): boolean => {
      if (!drilldown) return false;
      const regionName = row.region as string;
      return drilldown.isSelected("region", regionName);
    },
    [drilldown],
  );

  const columns: TableColumn[] = [
    {
      key: "region",
      header: "Região",
      align: "left",
      render: (value, item, index, isTotal) =>
        isTotal ? value : `${(page - 1) * pageSize + index + 1}. ${value}`,
    },
    {
      key: "item_category",
      header: "Categoria do Item",
      align: "left",
    },
    {
      key: "item_revenue",
      header: "Receita Do Item",
      align: "right",
      type: "currency",
      render: (value) => stdFormatCurrency(value),
    },
    {
      key: "item_revenue_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: (value) => {
        const num = typeof value === "number" ? value : null;
        if (num === null || !Number.isFinite(num))
          return <span className="text-muted-foreground">—</span>;
        return <TrendIndicator value={num} />;
      },
    },
  ];

  if (loading && !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>CATEGORIA PRODUTOS POR ESTADO</CardTitle>
          <CardDescription>Carregando...</CardDescription>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!data || !data.items || data.items.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>CATEGORIA PRODUTOS POR ESTADO</CardTitle>
          <CardDescription>
            Receita por categoria em cada estado
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-8">
            Nenhum dado disponível
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <StandardizedTable
      title="CATEGORIA PRODUTOS POR ESTADO"
      description={
        drilldown
          ? "Clique em uma linha para filtrar a página inteira (Ctrl+click para multi-seleção)"
          : "Receita por categoria em cada estado"
      }
      columns={columns}
      data={data.items}
      total={undefined}
      loading={loading}
      pagination={{
        current: data.pagination?.page || 1,
        pageSize: data.pagination?.page_size || pageSize,
        total: data.pagination?.total || 0,
        onPageChange: (newPage) => setPage(newPage),
      }}
      queryMode="server"
      onQueryChange={(q) => {
        setTableQuery(q);
        setPage(1);
      }}
      onRowClick={drilldown ? handleRowClick : undefined}
      clickableRows={!!drilldown}
      selectedRowIndex={drilldown ? isRowSelected : undefined}
    />
  );
};

export default CategoriasPorEstadoTable;
